/**
 * chaos-ui entry — bootstraps React 19 + TanStack Router and the PWA SW.
 *
 * The router tree is generated by TanStack Router Vite plugin into
 * src/routeTree.gen.ts (gitignored). Re-run `pnpm dev` once after any
 * route file is added/renamed to regenerate it.
 *
 * Service worker registration (Phase 11 / M9):
 *   - Runs only in production builds (avoids dev HMR conflicts).
 *   - Registers `/sw.js` at scope `/` so it controls the whole SPA.
 *   - Errors are swallowed (logged only) — the app must still work when
 *     SW registration fails (incognito, file://, browsers without push).
 */
import { createRouter, RouterProvider } from '@tanstack/react-router';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import { routeTree } from './routeTree.gen';
import './styles/global.css';

const router = createRouter({ routeTree });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Missing #root element in index.html');
}

createRoot(rootElement).render(
  <StrictMode>
    <RouterProvider router={router} />
  </StrictMode>,
);

// ── Service worker registration ───────────────────────────────────────
// We register in both dev and prod so the install-prompt and push flows
// can be exercised end-to-end. Dev still gets a real SW because Vite
// serves the static `public/sw.js` verbatim. If you ever switch to a
// build-step-generated SW (e.g. workbox), gate this on `import.meta.env.PROD`.
function registerServiceWorker(): void {
  if (typeof window === 'undefined') return;
  if (!('serviceWorker' in navigator)) return;
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js', { scope: '/' }).catch((err) => {
      // eslint-disable-next-line no-console
      console.warn('[chaos-ui] SW registration failed:', err);
    });
  });
}
registerServiceWorker();
